import { useState, FormEvent } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Layout from '../components/Layout';
import { api } from '../lib/api';

type VerifyResult = { found: boolean; itemName?: string; category?: string; postingDate?: string };

export default function RegisterProduct() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [step, setStep]           = useState<'serial' | 'details' | 'done'>('serial');
  const [serial, setSerial]       = useState('');
  const [verify, setVerify]       = useState<VerifyResult | null>(null);
  const [verifying, setVerifying] = useState(false);
  const [purchaseDate, setPurchaseDate] = useState('');
  const [notes, setNotes]         = useState('');
  const [proofFile, setProofFile] = useState<File | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError]         = useState('');
  const [regId, setRegId]         = useState<number | null>(null);

  async function checkSerial(e: FormEvent) {
    e.preventDefault();
    setError(''); setVerifying(true);
    try {
      const res = await api.get<VerifyResult>(`/products/verify/${encodeURIComponent(serial.trim().toUpperCase())}`);
      setVerify(res);
      if (res.found) setStep('details');
      else setError(t('registrations.serialInvalid'));
    } catch (err: any) { setError(err.message); }
    setVerifying(false);
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(''); setSubmitting(true);
    try {
      const reg = await api.post<{ id: number }>('/registrations', { serialNumber: serial, purchaseDate: purchaseDate || undefined, notes: notes || undefined });
      setRegId(reg.id);
      if (proofFile) {
        await api.upload(`/registrations/${reg.id}/upload-proof`, proofFile);
      }
      setStep('done');
    } catch (err: any) { setError(err.message); }
    setSubmitting(false);
  }

  if (step === 'done') return (
    <Layout>
      <div className="max-w-md mx-auto text-center py-16">
        <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
        </div>
        <h2 className="text-xl font-bold mb-2">{t('registrations.successTitle')}</h2>
        <p className="text-gray-500 text-sm mb-6">{t('registrations.successDesc')}</p>
        <button className="btn-primary" onClick={() => navigate('/registrations')}>{t('registrations.goToRegs')}</button>
      </div>
    </Layout>
  );

  return (
    <Layout>
      <div className="max-w-lg mx-auto">
        {/* FIVE benefits strip */}
        <div
          className="rounded-lg px-4 py-3 mb-6 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between"
          style={{ background: '#ffffff', border: '1px solid #e5e5e5' }}
        >
          <div>
            <p className="text-sm font-bold mb-1" style={{ color: '#0b0d0e' }}>
              🏆 Register to unlock FIVE — 3-year System Guarantee
            </p>
            <div className="flex flex-wrap gap-1.5">
              {['01 Tool', '02 Belt', '03 Performance', '04 Certified', '05 Trace'].map(badge => (
                <span
                  key={badge}
                  className="text-xs font-semibold px-2 py-0.5 rounded"
                  style={{ background: '#f4f4f4', color: '#0b0d0e', border: '1px solid #e5e5e5' }}
                >
                  {badge}
                </span>
              ))}
            </div>
          </div>
          <Link
            to="/five"
            className="text-xs font-semibold flex-shrink-0 mt-2 sm:mt-0"
            style={{ color: '#e00000', textDecoration: 'none', whiteSpace: 'nowrap' }}
          >
            Learn about FIVE →
          </Link>
        </div>

        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900">{t('registrations.newTitle')}</h1>
          <p className="text-gray-500 text-sm mt-1">Enter your product's serial number to get started.</p>
        </div>

        {/* Step indicator */}
        <div className="flex items-center gap-2 mb-8 text-sm">
          {['Enter serial', 'Confirm details'].map((s, i) => (
            <div key={s} className="flex items-center gap-2">
              <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold
                ${(step === 'serial' && i === 0) || (step === 'details' && i === 1)
                  ? 'bg-brand-red text-white' : 'bg-gray-100 text-gray-400'}`}>{i + 1}</span>
              <span className={step === 'serial' && i === 0 || step === 'details' && i === 1 ? 'text-gray-900 font-medium' : 'text-gray-400'}>{s}</span>
              {i === 0 && <span className="text-gray-200">›</span>}
            </div>
          ))}
        </div>

        {error && <p className="text-sm text-red-600 bg-red-50 rounded-lg px-3 py-2 mb-4">{error}</p>}

        {step === 'serial' && (
          <form onSubmit={checkSerial} className="card space-y-4">
            <div>
              <label className="label">{t('registrations.serialLabel')}</label>
              <input className="input font-mono" required value={serial} onChange={e => setSerial(e.target.value.toUpperCase())}
                placeholder={t('registrations.serialPlaceholder')} />
              <p className="text-xs text-gray-400 mt-1">Found on the product label or packaging.</p>
            </div>
            <button className="btn-primary w-full" type="submit" disabled={verifying || !serial.trim()}>
              {verifying ? t('registrations.verifying') : t('registrations.verify')}
            </button>
          </form>
        )}

        {step === 'details' && verify && (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="card bg-green-50 border-green-200">
              <p className="text-sm font-semibold text-green-800 mb-1">✓ {t('registrations.serialValid')}</p>
              <p className="text-sm text-green-700">{verify.itemName}</p>
              {verify.category && <p className="text-xs text-green-600 mt-0.5">{verify.category}</p>}
            </div>
            <div className="card space-y-4">
              <div>
                <label className="label">{t('registrations.purchaseDate')} <span className="text-gray-400 font-normal">(optional)</span></label>
                <input className="input" type="date" value={purchaseDate} onChange={e => setPurchaseDate(e.target.value)}
                  max={new Date().toISOString().slice(0, 10)} />
              </div>
              <div>
                <label className="label">{t('registrations.proofLabel')} <span className="text-gray-400 font-normal">(optional)</span></label>
                <input className="input" type="file" accept=".jpg,.jpeg,.png,.pdf"
                  onChange={e => setProofFile(e.target.files?.[0] ?? null)} />
                <p className="text-xs text-gray-400 mt-1">{t('registrations.proofDesc')}</p>
              </div>
              <div>
                <label className="label">Notes <span className="text-gray-400 font-normal">(optional)</span></label>
                <textarea className="input" rows={2} value={notes} onChange={e => setNotes(e.target.value)}
                  placeholder="Installation location, dealer name, etc." />
              </div>
              <div className="flex gap-3">
                <button type="button" className="btn-secondary flex-1" onClick={() => { setStep('serial'); setVerify(null); }}>{t('common.back')}</button>
                <button type="submit" className="btn-primary flex-1" disabled={submitting}>
                  {submitting ? t('registrations.submitting') : t('registrations.submit')}
                </button>
              </div>
            </div>
          </form>
        )}
      </div>
    </Layout>
  );
}
